// src/components/.../ExamPracticeUI.jsx
import React, {
   useContext,
   useEffect,
   useMemo,
   useRef,
   useState,
   useCallback,
} from "react";
import { UserContext } from "../../UserContext";
import {
   getMyPermissionStatus,
   grantExamPermissionExact,
   startExam,
   getExam,
   submitExamAnswer,
   isoFromNowPlusMinutes,
   isoToSecondsUTC,
   getTicketQuestions,
   getStudentExamHistory,
   failExam,
} from "../../api/examService";
import { updateUser } from "../../api/usersService";
import { ReactSVG } from "react-svg";
import heartFullIcon from "../../assets/svg/mingcute--heart-fill.svg";
import heartCrackIcon from "../../assets/svg/mingcute--heart-crack-fill.svg";
import addIcon from "../../assets/svg/add-s.svg";
import AlertPills from "../Utils/AlertPills";

/* ---------- helpers ---------- */
const EXAM_PERMISSION_HOURS = 12;
const prettyTime = (sec) => {
   const m = Math.floor(sec / 60);
   const s = sec % 60;
   return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const secsRemainingFromServer = (startedAtIso, timeLimitMin) => {
   if (!startedAtIso || !timeLimitMin)
      return timeLimitMin ? timeLimitMin * 60 : 0;
   const startedAt = Date.parse(startedAtIso);
   const elapsed = Math.floor((Date.now() - startedAt) / 1000);
   return clamp(timeLimitMin * 60 - elapsed, 0, timeLimitMin * 60);
};

const onlyDigits13 = (v) =>
   String(v || "")
      .replace(/\D/g, "")
      .slice(0, 13);
const isIdnp13 = (v) => /^\d{13}$/.test(String(v || ""));
const NO_IMAGE_PLACEHOLDER_URL =
   "https://auto-test.online/static/custom/img/noimage.jpg";

const normalizeExamImagePath = (raw) => {
   const src = String(raw || "").trim();
   if (!src) return null;

   const noHost = src.replace(/^https?:\/\/[^/]+/i, "");
   const withSlash = noHost.startsWith("/") ? noHost : `/${noHost}`;
   const withoutLegacyPrefix = withSlash.replace(
      /^\/EXAMEN_INSTRUIRE_AUTO(?=\/)/i,
      ""
   );
   const compact = withoutLegacyPrefix.replace(/\/{2,}/g, "/");
   if (!compact) return null;

   const withImagesPrefix = /^\/images\//i.test(compact)
      ? compact
      : `/images${compact.startsWith("/") ? compact : `/${compact}`}`;

   return withImagesPrefix.replace(
      /\/image(\d+)\.(?:jpe?g|png|webp)$/i,
      "/Image$1.jpeg"
   );
};

/* ---------- i18n (RO/RU) ---------- */
const I18N = {
   ro: {
      finish: "Încheie",
      question: "Întrebarea",
      lives: "Vieți",
      mistakes_label: "Greșeli",
      back: "Înapoi",
      next: "Următorul",
      answer_locked: "Răspuns blocat",
      choose_answer: "Alege răspunsul",
      passed: "Ai promovat",
      failed: "Nu ai promovat",
      questions_count: "Întrebări",
      mistakes_made: "Greșeli făcute",
      time_left: "Timp rămas",
      back_to_start: "Înapoi la început",

      permission_active: "Permisiune activă",
      no_permission_title: "Nu ai permisiunea",
    no_permission_body: "Nu ai permisiunea pentru test în acest moment.",
      check_again: "Verifică din nou",
      history_title: "Încercările tale la examen",
      loading_attempts: "Se încarcă încercările…",
      no_attempts: "Nu există încercări.",
      history_attempt: "Încercarea",
      history_ticket: "Bilet",
      history_tickets: "Bilete",
      history_score: "Scor",
      history_pass_score: "Prag",
      history_time_limit: "Timp",
      history_started: "Start",
      history_finished: "Final",
      history_minutes_suffix: "min",
      history_open: "În curs",
      history_unknown: "—",
      start_exam: "Începe examenul",
      edit_idnp: "Modifică IDNP",
      access_granted:
         "Acces acordat. Când ești pregătit, apasă „Începe examenul”.",
      start_title_checking: "Se verifică istoricul…",
      start_title_save_idnp: "Salvează mai întâi IDNP-ul",
      start_title_start: "Începe examenul",
      resume_checking: "Se verifică examenul activ…",
      resume_exam: "Examenul în curs a fost reluat.",

      leave_warning:
         "Chiar dorești să părăsești pagina examenului? Întrebarea curentă va fi marcată greșit (penalizare).",
      route_leave_confirm:
         "Chiar dorești să părăsești /student/exam? Întrebarea curentă va fi marcată greșit (penalizare).",
      penalty_msg:
         "Ai părăsit fereastra. Întrebarea curentă a fost marcată greșit (penalizare). Poți continua examenul.",
   },
   ru: {
      finish: "Завершить",
      question: "Вопрос",
      lives: "Жизни",
      mistakes_label: "Ошибки",
      back: "Назад",
      next: "Далее",
      answer_locked: "Ответ заблокирован",
      choose_answer: "Выберите ответ",
      passed: "Вы сдали",
      failed: "Вы не сдали",
      questions_count: "Вопросы",
      mistakes_made: "Сделанные ошибки",
      time_left: "Осталось времени",
      back_to_start: "Назад к началу",

      permission_active: "Разрешение активно",
      no_permission_title: "Нет разрешения",
    no_permission_body: "У вас нет разрешения на прохождение теста в данный момент.",
      check_again: "Проверить снова",
      history_title: "Ваши попытки экзамена",
      loading_attempts: "Загружаем попытки…",
      no_attempts: "Попыток нет.",
      history_attempt: "Попытка",
      history_ticket: "Билет",
      history_tickets: "Билеты",
      history_score: "Счёт",
      history_pass_score: "Порог",
      history_time_limit: "Время",
      history_started: "Старт",
      history_finished: "Финиш",
      history_minutes_suffix: "мин",
      history_open: "В процессе",
      history_unknown: "—",
      start_exam: "Начать экзамен",
      edit_idnp: "Изменить IDNP",
      access_granted:
         "Доступ разрешен. Когда будете готовы, нажмите «Начать экзамен».",
      start_title_checking: "Проверяем историю…",
      start_title_save_idnp: "Сначала сохраните IDNP",
      start_title_start: "Начать экзамен",
      resume_checking: "Проверяем активный экзамен…",
      resume_exam: "Текущий экзамен возобновлён.",

      leave_warning:
         "Вы действительно хотите покинуть страницу экзамена? Текущий вопрос будет засчитан неверным (штраф).",
      route_leave_confirm:
         "Вы действительно хотите уйти со /student/exam? Текущий вопрос будет засчитан неверным (штраф).",
      penalty_msg:
         "Вы покинули окно. Текущий вопрос засчитан неверным (штраф). Можете продолжить экзамен.",
   },
};
const makeT = (lang) => (key) => I18N[lang]?.[key] ?? I18N.ro[key] ?? key;

/** ✅ permisiune “activă” doar când avem fereastră validă & încadrări */
const computeIsAllowed = (perm) => {
   if (!perm) return false;
   if (perm.allowed === true) return true;
   const isActive = perm.isActive !== false;
   const hasValidUntil =
      typeof perm.validUntil === "string" &&
      !Number.isNaN(Date.parse(perm.validUntil));
   const stillValid = hasValidUntil && Date.parse(perm.validUntil) > Date.now();
   const hasMax =
      perm.maxAttempts != null && !Number.isNaN(Number(perm.maxAttempts));
   const used = Number(perm.usedAttempts ?? 0);
   const maxA = Number(perm.maxAttempts ?? 0);
   const attemptsOk = hasMax && used < maxA;
   return Boolean(isActive && stillValid && attemptsOk);
};

// normalizează indexul corect 0/1-based
const normalizeCorrectIdx = (raw, answersLen) => {
   const n = Number(raw);
   if (!Number.isInteger(n) || answersLen <= 0) return null;
   if (n >= 0 && n < answersLen) return n; // 0-based
   if (n >= 1 && n <= answersLen) return n - 1; // 1-based
   return null;
};

const resolveServerCorrectIdx = (
   rawCorrectIdx,
   answersLen,
   selectedIdx0,
   serverCorrect
) => {
   const n = Number(rawCorrectIdx);
   if (!Number.isInteger(n) || answersLen <= 0) return null;

   const idxOneBased = n >= 1 && n <= answersLen ? n - 1 : null;
   const idxZeroBased = n >= 0 && n < answersLen ? n : null;

   if (idxOneBased === idxZeroBased) return idxOneBased;

   if (typeof serverCorrect === "boolean" && Number.isInteger(selectedIdx0)) {
      if (serverCorrect) {
         if (selectedIdx0 === idxOneBased) return idxOneBased;
         if (selectedIdx0 === idxZeroBased) return idxZeroBased;
      } else {
         if (selectedIdx0 === idxOneBased && Number.isInteger(idxZeroBased))
            return idxZeroBased;
         if (selectedIdx0 === idxZeroBased && Number.isInteger(idxOneBased))
            return idxOneBased;
      }
   }

   if (Number.isInteger(idxOneBased)) return idxOneBased;
   if (Number.isInteger(idxZeroBased)) return idxZeroBased;
   return null;
};

const pickLang = (value, lang = "ro") => {
   if (value == null) return "";

   if (typeof value === "string") {
      const text = value.trim();
      if (
         text.startsWith("{") &&
         (text.includes('"ro"') || text.includes('"ru"'))
      ) {
         try {
            const parsed = JSON.parse(text);
            if (parsed && typeof parsed === "object") {
               return String(parsed?.[lang] ?? parsed?.ro ?? parsed?.ru ?? "");
            }
         } catch {}
      }
      return text;
   }

   if (typeof value === "object") {
      return String(value?.[lang] ?? value?.ro ?? value?.ru ?? "");
   }

   try {
      return String(value);
   } catch {
      return "";
   }
};

const normalizeExamQuestions = (rawQs = [], lang = "ro") => {
   const rows = Array.isArray(rawQs) ? rawQs : [];

   return rows.map((q) => {
      const rawImage = String(q?.image || "").trim();
      const primaryRaw = rawImage || null;
      const primaryImage = primaryRaw ? normalizeExamImagePath(primaryRaw) : null;
      const answersRaw = Array.isArray(q?.answers)
         ? q.answers
         : [q?.a1, q?.a2, q?.a3, q?.a4].filter((v) => v != null);

      return {
         id: (() => {
            const rawId = q?.id ?? q?.questionId ?? q?.examQuestionId;
            const parsedId = Number(rawId);
            return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : rawId;
         })(),
         text: pickLang(q?.text ?? q?.question ?? q?.title ?? "", lang),
         image: primaryImage,
         answers: answersRaw.map((a) => pickLang(a, lang)),
         order: q.order,
      };
   });
};

// dacă există ordine duplicată (ex: două bilete cu order 1..24),
// nu sortăm ca să păstrăm ordinea serverului (bilet1 apoi bilet2).
const shouldSortByOrder = (rawQs) => {
   const counts = new Map();
   let hasOrder = false;
   for (const q of rawQs || []) {
      const o = Number(q?.order);
      if (!Number.isFinite(o)) continue;
      hasOrder = true;
      const c = (counts.get(o) || 0) + 1;
      if (c > 1) return false;
      counts.set(o, c);
   }
   return hasOrder;
};

const toFiniteNumber = (value) => {
   const n = Number(value);
   return Number.isFinite(n) ? n : null;
};

const extractAttemptTicketNames = (it) => {
   const rows = Array.isArray(it?.examTickets) ? it.examTickets : [];
   const names = rows
      .map((row) => {
         const raw = row?.ticket?.name ?? row?.ticketName ?? row?.name ?? "";
         return String(raw || "").trim();
      })
      .filter(Boolean);

   if (names.length) return names;

   const fallback = String(it?.ticketName ?? it?.name ?? "").trim();
   return fallback ? [fallback] : [];
};

/* ===== helpers: istoric încercări ===== */
const normalizeAttempt = (it) => ({
   id:
      it.id ??
      `${it.examId || "exam"}-${it.startedAt || it.createdAt || Date.now()}`,
   examId: it.examId ?? it.id ?? null,
   startedAt: it.startedAt ?? it.createdAt ?? it.started ?? null,
   finishedAt: it.finishedAt ?? it.completedAt ?? it.endedAt ?? null,
   status: (
      it.status ?? (it.finishedAt ? "FINISHED" : "IN_PROGRESS")
   ).toUpperCase(),
   total: toFiniteNumber(it.total ?? it.totalQuestions ?? it.questionsTotal),
   score: toFiniteNumber(it.score ?? it.correct ?? it.correctCount ?? it.right),
   correct: toFiniteNumber(it.correct ?? it.correctCount ?? it.right),
   wrong: toFiniteNumber(it.wrong ?? it.wrongCount ?? it.incorrect),
   passScore: toFiniteNumber(it.passScore ?? it.pass),
   timeLimit: toFiniteNumber(it.timeLimit ?? it.timeLimitMin ?? it.duration),
   attemptNumber: toFiniteNumber(
      it.attemptNumber ?? it.tryNumber ?? it.attempt ?? it.number
   ),
   ticketNames: extractAttemptTicketNames(it),
   scorePct:
      typeof it.scorePct === "number"
         ? it.scorePct
         : typeof it.percentage === "number"
         ? it.percentage
         : typeof it.score === "number"
         ? it.score
         : null,
});

const EXAM_DRAFT_STORAGE_PREFIX = "student_exam_draft_v1";

const normalizeSelectedToZeroBased = (raw, answersLen) => {
   const n = Number(raw);
   if (!Number.isInteger(n) || answersLen <= 0) return null;
   if (n >= 0 && n < answersLen) return n;
   if (n >= 1 && n <= answersLen) return n - 1;
   return null;
};

const isInProgressExamStatus = (status) => {
   const up = String(status || "").toUpperCase();
   return (
      up.includes("IN_PROGRESS") ||
      up.includes("STARTED") ||
      up.includes("ACTIVE")
   );
};

const isExamOpenForAnswers = (exam) => {
   if (!exam || typeof exam !== "object") return false;
   const st = String(exam?.status || "").toUpperCase();
   if (st) {
      if (
         st.includes("FINISHED") ||
         st.includes("FAILED") ||
         st.includes("PASSED") ||
         st.includes("COMPLETED")
      ) {
         return false;
      }
      if (isInProgressExamStatus(st)) return true;
   }
   if (exam?.finishedAt || exam?.completedAt || exam?.endedAt) return false;
   return true;
};

const buildExamDraftStorageKey = (userId) => {
   const uid = Number(userId);
   if (!Number.isInteger(uid) || uid <= 0) return null;
   return `${EXAM_DRAFT_STORAGE_PREFIX}_${uid}`;
};

const readExamDraft = (userId) => {
   const key = buildExamDraftStorageKey(userId);
   if (!key) return null;
   try {
      if (typeof localStorage === "undefined") return null;
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
   } catch {
      return null;
   }
};

const saveExamDraft = (userId, payload) => {
   const key = buildExamDraftStorageKey(userId);
   if (!key) return;
   try {
      if (typeof localStorage === "undefined") return;
      localStorage.setItem(key, JSON.stringify(payload));
   } catch {}
};

const clearExamDraft = (userId) => {
   const key = buildExamDraftStorageKey(userId);
   if (!key) return;
   try {
      if (typeof localStorage === "undefined") return;
      localStorage.removeItem(key);
   } catch {}
};

const collectExamAnswerRows = (exam) => {
   const rows = [];
   const pushRows = (value) => {
      if (Array.isArray(value)) rows.push(...value);
   };
   pushRows(exam?.examAnswers);
   pushRows(exam?.userAnswers);
   pushRows(exam?.responses);
   if (
      Array.isArray(exam?.answers) &&
      exam.answers.some(
         (row) =>
            row &&
            typeof row === "object" &&
            (row.questionId != null ||
               row.questionID != null ||
               row.question?.id != null)
      )
   ) {
      pushRows(exam.answers);
   }
   return rows;
};

const buildAnswersMapFromExamData = (exam, normalizedQuestions = []) => {
   const map = {};
   const questionById = new Map(
      (normalizedQuestions || [])
         .map((q) => [Number(q?.id), q])
         .filter(([id]) => Number.isInteger(id) && id > 0)
   );

   const upsert = (qid, selectedIdx0, meta = {}) => {
      if (!Number.isInteger(qid) || qid <= 0 || selectedIdx0 == null) return;
      if (map[qid]?.selected != null) return;
      let correct = null;
      if (typeof meta.correct === "boolean") correct = meta.correct;
      else if (typeof meta.isCorrect === "boolean") correct = meta.isCorrect;
      map[qid] = {
         selected: Number(selectedIdx0),
         correct,
         at:
            meta.answeredAt ??
            meta.createdAt ??
            meta.at ??
            new Date().toISOString(),
         serverCorrectIdx: Number.isInteger(meta.serverCorrectIdx)
            ? meta.serverCorrectIdx
            : null,
         explanation: meta.explanation ?? null,
      };
   };

   for (const row of collectExamAnswerRows(exam)) {
      const qid = Number(
         row?.questionId ?? row?.question?.id ?? row?.questionID
      );
      const q = questionById.get(qid);
      const answersLen = Array.isArray(q?.answers) ? q.answers.length : 4;
      const selected = normalizeSelectedToZeroBased(
         row?.selectedAnswer ?? row?.selected ?? row?.answer ?? row?.userAnswer,
         answersLen
      );
      if (selected == null) continue;
      const serverCorrectIdx = normalizeCorrectIdx(
         row?.correctAnswer ?? row?.correctIndex ?? row?.rightIndex,
         answersLen
      );
      upsert(qid, selected, {
         correct: row?.correct,
         isCorrect: row?.isCorrect,
         answeredAt: row?.answeredAt ?? row?.createdAt,
         serverCorrectIdx,
         explanation: row?.explanation,
      });
   }

   const rawQs = Array.isArray(exam?.questions) ? exam.questions : [];
   for (const q of rawQs) {
      const qid = Number(q?.id ?? q?.questionId);
      const question = questionById.get(qid);
      const answersLen = Array.isArray(question?.answers)
         ? question.answers.length
         : Array.isArray(q?.answers)
         ? q.answers.length
         : 4;
      const selected = normalizeSelectedToZeroBased(
         q?.selectedAnswer ?? q?.userAnswer ?? q?.answeredIndex,
         answersLen
      );
      if (selected == null) continue;
      const serverCorrectIdx = normalizeCorrectIdx(
         q?.correctAnswer ?? q?.correctIndex ?? q?.rightIndex,
         answersLen
      );
      upsert(qid, selected, {
         correct: q?.correct,
         isCorrect: q?.isCorrect,
         answeredAt: q?.answeredAt ?? q?.createdAt,
         serverCorrectIdx,
         explanation: q?.explanation,
      });
   }

   return map;
};

const buildCorrectMapFromExamData = (exam, normalizedQuestions = []) => {
   const map = {};
   const questionById = new Map(
      (normalizedQuestions || [])
         .map((q) => [Number(q?.id), q])
         .filter(([id]) => Number.isInteger(id) && id > 0)
   );

   const upsert = (qid, rawCorrect, answersLen) => {
      const ci = normalizeCorrectIdx(rawCorrect, answersLen);
      if (Number.isInteger(qid) && qid > 0 && Number.isInteger(ci)) {
         map[qid] = ci;
      }
   };

   for (const row of collectExamAnswerRows(exam)) {
      const qid = Number(
         row?.questionId ?? row?.question?.id ?? row?.questionID
      );
      const q = questionById.get(qid);
      const answersLen = Array.isArray(q?.answers) ? q.answers.length : 4;
      upsert(
         qid,
         row?.correctAnswer ?? row?.correctIndex ?? row?.rightIndex,
         answersLen
      );
   }

   const rawQs = Array.isArray(exam?.questions) ? exam.questions : [];
   for (const q of rawQs) {
      const qid = Number(q?.id ?? q?.questionId);
      const question = questionById.get(qid);
      const answersLen = Array.isArray(question?.answers)
         ? question.answers.length
         : Array.isArray(q?.answers)
         ? q.answers.length
         : 4;
      upsert(
         qid,
         q?.correctAnswer ?? q?.correctIndex ?? q?.rightIndex,
         answersLen
      );
   }

   return map;
};

const mergeAnswersMaps = (serverMap = {}, draftMap = {}) => {
   // Draft-ul local păstrează starea vizuală (corect/greșit) primită la submit.
   // Serverul la getExam poate returna doar selectedAnswer, fără correct.
   const out = {};
   const allKeys = new Set([
      ...Object.keys(draftMap || {}),
      ...Object.keys(serverMap || {}),
   ]);

   for (const key of allKeys) {
      const qid = Number(key);
      if (!Number.isInteger(qid) || qid <= 0) continue;

      const local = draftMap?.[qid] ?? draftMap?.[key];
      const server = serverMap?.[qid] ?? serverMap?.[key];
      const localSelected =
         local?.selected != null ? Number(local.selected) : null;
      const serverSelected =
         server?.selected != null ? Number(server.selected) : null;

      if (localSelected == null && serverSelected == null) continue;

      const selected =
         localSelected != null ? localSelected : serverSelected;

      let correct = null;
      if (typeof local?.correct === "boolean") correct = local.correct;
      else if (typeof server?.correct === "boolean") correct = server.correct;
      else if (typeof server?.isCorrect === "boolean") correct = server.isCorrect;

      const serverCorrectIdx = Number.isInteger(local?.serverCorrectIdx)
         ? local.serverCorrectIdx
         : Number.isInteger(server?.serverCorrectIdx)
         ? server.serverCorrectIdx
         : null;

      out[qid] = {
         selected,
         correct,
         at: local?.at ?? server?.at ?? new Date().toISOString(),
         serverCorrectIdx,
         explanation: local?.explanation ?? server?.explanation ?? null,
      };
   }

   return out;
};

const normalizeAnswersMapKeys = (map = {}) => {
   const out = {};
   for (const [key, entry] of Object.entries(map || {})) {
      const qid = Number(key);
      if (!Number.isInteger(qid) || qid <= 0 || !entry) continue;
      if (entry.selected == null) continue;
      out[qid] = {
         ...entry,
         selected: Number(entry.selected),
      };
   }
   return out;
};

const getDraftAnswersForExam = (userId, examId, draft, initialAnswersMap) => {
   if (initialAnswersMap) return normalizeAnswersMapKeys(initialAnswersMap);
   const targetExamId = Number(examId);
   if (!Number.isInteger(targetExamId) || targetExamId <= 0) return {};

   const sources = [draft, readExamDraft(userId)].filter(Boolean);
   for (const item of sources) {
      if (Number(item?.examId) !== targetExamId) continue;
      if (item?.answersMap && typeof item.answersMap === "object") {
         return normalizeAnswersMapKeys(item.answersMap);
      }
   }
   return {};
};

const hydrateAnswersCorrectness = (answersMap = {}, correctMap = {}) => {
   const out = { ...(answersMap || {}) };
   for (const [key, entry] of Object.entries(out)) {
      const qid = Number(key);
      if (entry?.selected == null || entry.correct != null) continue;

      if (Number.isInteger(entry.serverCorrectIdx)) {
         out[qid] = {
            ...entry,
            correct:
               Number(entry.serverCorrectIdx) === Number(entry.selected),
         };
         continue;
      }

      if (Object.prototype.hasOwnProperty.call(correctMap, qid)) {
         out[qid] = {
            ...entry,
            correct: Number(correctMap[qid]) === Number(entry.selected),
         };
      }
   }
   return out;
};

const findFirstUnansweredIdx = (questions = [], answersMap = {}) => {
   const qs = Array.isArray(questions) ? questions : [];
   for (let i = 0; i < qs.length; i += 1) {
      const qid = Number(qs[i]?.id);
      const saved = answersMap?.[qid];
      if (!saved || saved.selected == null) return i;
   }
   return Math.max(0, qs.length - 1);
};

const findInProgressAttempt = (attempts = []) => {
   const list = Array.isArray(attempts) ? attempts : [];
   return (
      list
         .map(normalizeAttempt)
         .filter((a) => {
            if (a.finishedAt) return false;
            return isInProgressExamStatus(a.status);
         })
         .sort((a, b) => {
            const ta = a.startedAt ? Date.parse(a.startedAt) : 0;
            const tb = b.startedAt ? Date.parse(b.startedAt) : 0;
            return tb - ta;
         })[0] || null
   );
};

/* ---------- config ---------- */
const EXAM_TOTAL_QUESTIONS = 48;
const EXAM_PASS_SCORE = 44;
const DEFAULT_MAX_LIVES = 5;
const derivePassScore = () => EXAM_PASS_SCORE;

/** Traduceri status -> RO */
const roStatus = (s) => {
   const k = String(s || "").toLowerCase();
   if (k.includes("failed") || k === "fail") return "respins";
   if (k.includes("completed") || k === "finished" || k === "done")
      return "admis";
   if (
      k.includes("in_progress") ||
      k.includes("in-progress") ||
      k === "inprogress"
   )
      return "în desfășurare";
   return s || "—";
};
/** Traduceri status -> RU */
const ruStatus = (s) => {
   const k = String(s || "").toLowerCase();
   if (k.includes("failed") || k === "fail") return "не сдан";
   if (k.includes("completed") || k === "finished" || k === "done")
      return "сдан";
   if (
      k.includes("in_progress") ||
      k.includes("in-progress") ||
      k === "inprogress"
   )
      return "в процессе";
   return s || "—";
};

export default function ExamPracticeUI({
   maxLives = DEFAULT_MAX_LIVES,
   useHearts = true,
}) {
   const { user, setUser } = useContext(UserContext) || {};

   const [view, setView] = useState("waiting"); // waiting | test | result
   const [resumeChecking, setResumeChecking] = useState(true);
   const [perm, setPerm] = useState(null);
   const [pillMsgs, setPillMsgs] = useState([]);

   const [exam, setExam] = useState(null);
   const [questions, setQuestions] = useState([]);
   const [idx, setIdx] = useState(0);
   const [answersMap, setAnswersMap] = useState({});
   const [remaining, setRemaining] = useState(0);
   const [answerLoading, setAnswerLoading] = useState(null);

   const [correctMap, setCorrectMap] = useState({});
   const [, setCorrectMapLoaded] = useState(false);

   const [attempts, setAttempts] = useState([]);
   const [attemptsLoading, setAttemptsLoading] = useState(false);
   const [attemptsError, setAttemptsError] = useState("");

   /* ---------- UI: tranziție spre rezultat ---------- */
   const FADE_MS = 380;
   const [resultTransitioning, setResultTransitioning] = useState(false);
   const [resultAnimOn, setResultAnimOn] = useState(false);

   /* ---------- IDNP gate state ---------- */
   const hasIdnp = useMemo(() => isIdnp13(user?.idnp), [user?.idnp]);
   const [idnp, setIdnp] = useState(hasIdnp ? String(user?.idnp) : "");
   const [idnpBusy, setIdnpBusy] = useState(false);
   const [idnpMsg, setIdnpMsg] = useState(null);
   const [manualIdnpEditor, setManualIdnpEditor] = useState(false); // ✅ buton "Modifică IDNP"

   const qTextRef = useRef(null);
   const timerRef = useRef(null);
   const finishingRef = useRef(false);

   // refs pentru listeners
   const answersMapRef = useRef(answersMap);
   const questionsRef = useRef(questions);
   const examRef = useRef(exam);
   const correctMapRef = useRef(correctMap);
   const silentAutoSubmitRef = useRef(false);
   const viewRef = useRef(view);
   const idxRef = useRef(idx);
   const answerLoadingRef = useRef(answerLoading);
   const resumeCheckedRef = useRef(false);

  useEffect(() => {
    const on = view === "test" || view === "result";
    document.body.classList.toggle("is-exam-mode", on);
    return () => document.body.classList.remove("is-exam-mode");
  }, [view]);

   // limba selectată (persistată) + limba UI înghețată pe durata testului
   const [lang, setLang] = useState(() => {
      const saved =
         (typeof localStorage !== "undefined" &&
            localStorage.getItem("exam.lang")) ||
         "ro";
      return saved === "ru" ? "ru" : "ro";
   });
   const [examUiLang, setExamUiLang] = useState(null);
   const currentLang =
      view === "test" || view === "result" ? examUiLang || lang : lang;
   const t = useMemo(() => makeT(currentLang), [currentLang]);

   useEffect(() => {
      try {
         localStorage.setItem("exam.lang", lang);
      } catch {}
   }, [lang]);

   useEffect(() => {
      answersMapRef.current = answersMap;
   }, [answersMap]);
   useEffect(() => {
      questionsRef.current = questions;
   }, [questions]);
   useEffect(() => {
      examRef.current = exam;
   }, [exam]);
   useEffect(() => {
      correctMapRef.current = correctMap;
   }, [correctMap]);
   useEffect(() => {
      viewRef.current = view;
   }, [view]);
   useEffect(() => {
      idxRef.current = idx;
   }, [idx]);
   useEffect(() => {
      answerLoadingRef.current = answerLoading;
   }, [answerLoading]);

   // ținem inputul sincron când user.idnp se schimbă
   useEffect(() => {
      if (hasIdnp) setIdnp(String(user?.idnp || ""));
   }, [hasIdnp, user?.idnp]);

   const scrollToQText = () => {
      const el = qTextRef.current;
      if (!el) return;
      const top = Math.max(0, window.scrollY + (el.getBoundingClientRect().top || 0));
      if (Math.abs(window.scrollY - top) > 1)
         window.scrollTo({ top, behavior: "smooth" });
   };

   const pushError = useCallback((text) => {
      const msg = String(text || "").trim();
      if (!msg) return;
      setPillMsgs((arr) => [
         ...arr,
         { id: Date.now() + Math.random(), type: "error", text: msg },
      ]);
   }, []);

   const dismissLastPill = useCallback(() => {
      setPillMsgs((arr) => arr.slice(0, -1));
   }, []);

   useEffect(() => {
      if (!pillMsgs.length) return;
      const tmo = setTimeout(dismissLastPill, 4000);
      return () => clearTimeout(tmo);
   }, [dismissLastPill, pillMsgs]);

   useEffect(() => {
      return () => {
         timerRef.current && clearInterval(timerRef.current);
      };
   }, []);

   const startTimer = (secs) => {
      setRemaining(secs);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(
         () => setRemaining((r) => (r <= 1 ? 0 : r - 1)),
         1000
      );
   };

   /* ---------- permisiune: polling continuu la 3s ---------- */
   useEffect(() => {
      let cancelled = false;
      let tmo;
      const tick = async () => {
         try {
            const p = await getMyPermissionStatus();
            if (!cancelled) setPerm(p);
         } catch {
            // nu stricăm UI dacă e eroare la fetch
         } finally {
            if (!cancelled) tmo = setTimeout(tick, 3000);
         }
      };
      tick();
      return () => {
         cancelled = true;
         if (tmo) clearTimeout(tmo);
      };
   }, []);

   /* ---------- istoric ---------- */
   useEffect(() => {
      if (view !== "waiting" || !user?.id) return;
      let cancelled = false;
      (async () => {
         setAttemptsLoading(true);
         setAttemptsError("");
         try {
            const pageSize = 50;
            let page = 1;
            const all = [];
            for (;;) {
               const batch = await getStudentExamHistory({
                  page,
                  limit: pageSize,
               });
               const items = Array.isArray(batch)
                  ? batch
                  : batch?.data || batch?.items || batch?.results || [];
               if (!items?.length) break;
               all.push(...items);

               const totalPages =
                  batch?.pagination?.totalPages ??
                  batch?.meta?.totalPages ??
                  batch?.totalPages ??
                  null;
               if (totalPages ? page >= totalPages : items.length < pageSize)
                  break;
               page += 1;
            }
            const normalized = all.map(normalizeAttempt).sort((a, b) => {
               const ta = a.startedAt ? Date.parse(a.startedAt) : 0;
               const tb = b.startedAt ? Date.parse(b.startedAt) : 0;
               return tb - ta;
            });
            if (!cancelled) setAttempts(normalized);
         } catch (e) {
            if (!cancelled)
               setAttemptsError(
                  e?.message || "Nu am putut încărca încercările."
               );
         } finally {
            if (!cancelled) setAttemptsLoading(false);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [view, user?.id]);

   /* === gate logic: input IDNP doar când NU există încercări; dar se poate deschide manual === */
   const hasAnyAttempt = attempts.length > 0;
   const baseShowIdnpGate = useMemo(() => {
      if (attemptsLoading) return false; // așteptăm încărcarea
      if (attemptsError) return true; // conservator: cerem IDNP
      return !hasAnyAttempt; // fără încercări → arătăm inputul
   }, [attemptsLoading, attemptsError, hasAnyAttempt]);
   const showIdnpGate = baseShowIdnpGate || manualIdnpEditor; // ✅ poate fi forțat din buton

   /* ---------- cheie corectă ---------- */
   const getTicketIdsFromExam = (started) => {
      const ids = new Set();
      const add = (value) => {
         const n = Number(value);
         if (Number.isInteger(n) && n > 0) ids.add(n);
      };
      add(started?.ticketId);
      add(started?.ticket?.id);
      add(started?.ticketID);
      for (const row of started?.examTickets || []) {
         add(row?.ticketId);
         add(row?.ticket?.id);
      }
      return [...ids];
   };

   const persistExamDraftNow = useCallback(
      (patch = {}) => {
         const uid = Number(user?.id);
         const liveExam = examRef.current;
         if (!Number.isInteger(uid) || uid <= 0 || !liveExam?.id) return;

         const existing = readExamDraft(uid) || {};
         saveExamDraft(uid, {
            ...existing,
            examId: Number(liveExam.id),
            idx: idxRef.current,
            answersMap: answersMapRef.current || {},
            examUiLang: examUiLang || lang,
            savedAt: new Date().toISOString(),
            ...patch,
         });
      },
      [user?.id, examUiLang, lang]
   );

   const buildCorrectMap = async (started) => {
      try {
         const ticketIds = getTicketIdsFromExam(started);
         if (!ticketIds.length) return {};

         const m = {};
         for (const tid of ticketIds) {
            const qs = await getTicketQuestions(tid);
            (qs || []).forEach((q) => {
               const qid = Number(q?.id);
               const ci = normalizeCorrectIdx(
                  q?.correctAnswer,
                  Number(q?.answers?.length || 0)
               );
               if (Number.isInteger(qid) && Number.isInteger(ci)) m[qid] = ci;
            });
         }
         setCorrectMap((prev) => ({ ...(prev || {}), ...m }));
         return m;
      } catch (e) {
         return {};
      } finally {
         setCorrectMapLoaded(true);
      }
   };

   const verifyAnswerLocal = (qId, selectedIdx0) => {
      if (!Object.prototype.hasOwnProperty.call(correctMap, qId)) return null;
      return Number(correctMap[qId]) === Number(selectedIdx0);
   };

   const fetchInProgressAttemptId = useCallback(async () => {
      const draft = readExamDraft(user?.id);
      const draftExamId = Number(draft?.examId);
      if (Number.isInteger(draftExamId) && draftExamId > 0) {
         return { examId: draftExamId, draft };
      }

      try {
         const batch = await getStudentExamHistory({ page: 1, limit: 50 });
         const items = Array.isArray(batch)
            ? batch
            : batch?.data || batch?.items || batch?.results || [];
         const attempt = findInProgressAttempt(items);
         const examId = Number(attempt?.examId ?? attempt?.id);
         if (Number.isInteger(examId) && examId > 0) {
            return { examId, draft: null };
         }
      } catch {}

      return null;
   }, [user?.id]);

   const applyExamSession = useCallback(
      async (started, options = {}) => {
         const {
            initialAnswersMap = null,
            initialIdx = null,
            draft = null,
            notifyResume = false,
         } = options;

         let examData = started;
         const sessionLang =
            started?.lang === "ru" || started?.lang === "ro"
               ? started.lang
               : draft?.examUiLang === "ru"
               ? "ru"
               : lang;

         if (started?.lang === "ru" || started?.lang === "ro") {
            setExamUiLang(started.lang);
         } else if (draft?.examUiLang === "ru" || draft?.examUiLang === "ro") {
            setExamUiLang(draft.examUiLang);
         } else {
            setExamUiLang(lang);
         }

         setExam(examData);

         let serverQs = Array.isArray(examData?.questions)
            ? examData.questions
            : [];
         if (!serverQs.length && examData?.id) {
            const fresh = await getExam(examData.id);
            examData = fresh;
            setExam(fresh);
            serverQs = Array.isArray(fresh?.questions) ? fresh.questions : [];
         }

         const baseQs = shouldSortByOrder(serverQs)
            ? [...serverQs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            : serverQs;
         const normalized = normalizeExamQuestions(baseQs || [], sessionLang);
         setQuestions(normalized);

         const limitMin = Number(examData?.timeLimit ?? started?.timeLimit ?? 60);
         const secs = secsRemainingFromServer(
            examData?.startedAt ?? started?.startedAt,
            limitMin
         );
         startTimer(secs);

         const serverAnswers = buildAnswersMapFromExamData(examData, normalized);
         const draftAnswers = getDraftAnswersForExam(
            user?.id,
            examData?.id ?? started?.id,
            draft,
            initialAnswersMap
         );
         const storedDraft =
            draft ||
            (() => {
               const local = readExamDraft(user?.id);
               return Number(local?.examId) === Number(examData?.id)
                  ? local
                  : null;
            })();
         const fromExamCorrect = buildCorrectMapFromExamData(
            examData,
            normalized
         );
         if (Object.keys(fromExamCorrect).length) {
            setCorrectMap(fromExamCorrect);
         }
         setCorrectMapLoaded(false);
         const ticketCorrect = await buildCorrectMap(examData);
         const combinedCorrect = { ...fromExamCorrect, ...ticketCorrect };
         const mergedAnswers = hydrateAnswersCorrectness(
            mergeAnswersMaps(serverAnswers, draftAnswers),
            combinedCorrect
         );
         setAnswersMap(mergedAnswers);

         const preferredIdx =
            Number.isInteger(initialIdx) && initialIdx >= 0
               ? initialIdx
               : Number.isInteger(storedDraft?.idx)
               ? storedDraft.idx
               : findFirstUnansweredIdx(normalized, mergedAnswers);
         const safeIdx = clamp(
            preferredIdx,
            0,
            Math.max(0, normalized.length - 1)
         );
         setIdx(safeIdx);

         silentAutoSubmitRef.current = false;
         setView("test");

         const uid = Number(user?.id);
         if (Number.isInteger(uid) && uid > 0 && examData?.id) {
            saveExamDraft(uid, {
               examId: Number(examData.id),
               idx: safeIdx,
               answersMap: normalizeAnswersMapKeys(mergedAnswers),
               examUiLang: sessionLang,
               savedAt: new Date().toISOString(),
            });
         }

         if (notifyResume) {
            const resumeT = makeT(sessionLang);
            setPillMsgs((arr) => [
               ...arr,
               {
                  id: Date.now() + Math.random(),
                  type: "success",
                  text: resumeT("resume_exam"),
               },
            ]);
         }

         setTimeout(
            () =>
               requestAnimationFrame(() =>
                  requestAnimationFrame(scrollToQText)
               ),
            0
         );

         return examData;
      },
      [lang, user?.id]
   );

   const restoreActiveExam = useCallback(
      async (fresh, draft = null, notifyResume = false) => {
         if (!isExamOpenForAnswers(fresh)) {
            if (user?.id) clearExamDraft(user.id);
            return false;
         }
         setCorrectMap({});
         await applyExamSession(fresh, {
            draft,
            initialIdx: draft?.idx,
            notifyResume,
         });
         return true;
      },
      [applyExamSession, user?.id]
   );

   useEffect(() => {
      if (!user?.id) {
         setResumeChecking(false);
         return;
      }
      if (resumeCheckedRef.current) return;
      resumeCheckedRef.current = true;

      let cancelled = false;
      (async () => {
         try {
            const found = await fetchInProgressAttemptId();
            if (!found || cancelled) return;

            const fresh = await getExam(found.examId);
            if (cancelled) return;

            const ok = await restoreActiveExam(fresh, found.draft, true);
            if (!ok && user?.id) clearExamDraft(user.id);
         } catch {
            // rămânem pe ecranul de start
         } finally {
            if (!cancelled) setResumeChecking(false);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [user?.id, fetchInProgressAttemptId, restoreActiveExam]);

   useEffect(() => {
      const uid = Number(user?.id);
      if (view !== "test" || !exam?.id || !Number.isInteger(uid) || uid <= 0) {
         return;
      }
      saveExamDraft(uid, {
         examId: Number(exam.id),
         idx,
         answersMap: normalizeAnswersMapKeys(answersMap),
         examUiLang: examUiLang || lang,
         savedAt: new Date().toISOString(),
      });
   }, [view, exam?.id, user?.id, idx, answersMap, examUiLang, lang]);

   /* ---------- START exam ---------- */
   const handleStart = async () => {
      setPillMsgs([]);

      if (showIdnpGate && !isIdnp13(user?.idnp)) {
         pushError(
            "Completează și salvează IDNP (13 cifre) înainte de a începe examenul."
         );
         return;
      }

      setCorrectMap({});
      setCorrectMapLoaded(false);
      try {
         const requestPassScore = EXAM_PASS_SCORE;

         // re-check permisiune chiar la start
         let p = await getMyPermissionStatus();
         const allowedNow = computeIsAllowed(p);
         if (!allowedNow) {
            if (!user?.id)
               throw new Error("Nu știu ID-ul utilizatorului curent.");
            await grantExamPermissionExact({
               userId: Number(user.id),
               validUntil: isoToSecondsUTC(
                  isoFromNowPlusMinutes(EXAM_PERMISSION_HOURS * 60)
               ),
               maxAttempts: 3,
            });
            // mică așteptare până devine activ
            const t0 = Date.now();
            do {
               await new Promise((r) => setTimeout(r, 400));
               p = await getMyPermissionStatus();
            } while (!computeIsAllowed(p) && Date.now() - t0 < 6000);
            if (!computeIsAllowed(p))
               throw new Error(
                  "Permisiunea nu a devenit activă încă. Reîncearcă în câteva secunde."
               );
         }
         setPerm(p);

         const active = await fetchInProgressAttemptId();
         if (active?.examId) {
            try {
               const fresh = await getExam(active.examId);
               if (isExamOpenForAnswers(fresh)) {
                  await restoreActiveExam(fresh, active.draft, true);
                  return;
               }
               if (user?.id) clearExamDraft(user.id);
            } catch {}
         }

         const started = await startExam({
            userId: Number(user.id),
            timeLimit: 60,
            passScore: requestPassScore ?? undefined,
            lang,
         });

         await applyExamSession(started);
      } catch (e) {
         pushError(e?.message || "Nu am putut porni examenul.");
      }
   };

   const current = questions[idx] || null;
   const total = questions.length;
   const passScore = derivePassScore(total, exam?.passScore);
   const mistakeLimit = Math.max(1, Number(maxLives) || 1);

   const mistakesMade = useMemo(
      () =>
         Object.values(answersMap).filter(
            (a) => a?.selected != null && a.correct === false
         ).length,
      [answersMap]
   );
   const livesLeft = Math.max(0, mistakeLimit - mistakesMade);

   // ✅ scor corect: fiecare răspuns corect = +1
   const correctAnsweredRaw = useMemo(
      () =>
         Object.values(answersMap).filter(
            (a) => a?.selected != null && a.correct === true
         ).length,
      [answersMap]
   );
   // Dacă a atins pragul de promovare, restul se consideră corecte la AFIȘARE
   const statusBoard = useMemo(() => {
      if (!questions.length) return [];
      return questions.map((q, i) => {
         const a = answersMap[q.id];
         let status = "none";
         if (a?.selected != null) {
            status =
               a.correct === true
                  ? "ok"
                  : a.correct === false
                  ? "bad"
                  : "answered";
         }
         return { i, status };
      });
   }, [questions, answersMap]);

   const jumpTo = (i, { force = false } = {}) => {
      if (!force && (answerLoadingRef.current != null || finishingRef.current))
         return;
      const clamped = Math.max(0, Math.min(i, total - 1));
      setIdx(clamped);
      requestAnimationFrame(() => requestAnimationFrame(scrollToQText));
   };
   const goPrev = () => jumpTo(Math.max(0, idx - 1));
   const goNext = () => jumpTo(Math.min(total - 1, idx + 1));

   const versionedImage = useCallback((src, key) => {
      if (!src) return "";
      return src + (src.includes("?") ? "&" : "?") + `v=${key}`;
   }, []);

   const onQuestionImageError = useCallback((e) => {
      const img = e.currentTarget;
      const src = String(img.currentSrc || img.src || "");
      const normalizedSrc = src.split("?")[0];
      const isPlaceholder = normalizedSrc === NO_IMAGE_PLACEHOLDER_URL;
      if (!isPlaceholder) {
         img.src = NO_IMAGE_PLACEHOLDER_URL;
         return;
      }
      img.hidden = true;
   }, []);

   useEffect(() => {
      if (!current) return;
      const raf = requestAnimationFrame(() =>
         requestAnimationFrame(scrollToQText)
      );
      return () => cancelAnimationFrame(raf);
   }, [idx, current]);

   const goToResult = useCallback(() => {
      if (viewRef.current === "result") return;
      if (user?.id) clearExamDraft(user.id);
      setResultTransitioning(true); // fade-out test
      setTimeout(() => {
         setView("result");
         setResultTransitioning(false);
         setResultAnimOn(false);
         // mic pop-in la rezultat
         setTimeout(() => setResultAnimOn(true), 30);
      }, FADE_MS);
   }, [user?.id]);

   const autoNext = (nextMap, fromIdx = idxRef.current) => {
      const qs = Array.isArray(questionsRef.current) ? questionsRef.current : [];
      if (!qs.length) return;

      const startIdx = clamp(
         Number.isInteger(fromIdx) ? fromIdx : 0,
         0,
         qs.length - 1
      );

      for (let i = startIdx + 1; i < qs.length; i++) {
         const q = qs[i];
         const a = nextMap[q.id];
         if (!a || a.selected == null) {
            jumpTo(i, { force: true });
            return;
         }
      }
      for (let i = 0; i < startIdx; i++) {
         const q = qs[i];
         const a = nextMap[q.id];
         if (!a || a.selected == null) {
            jumpTo(i, { force: true });
            return;
         }
      }
      goToResult(); // 🔄 tranziție spre rezultat
   };

   const endExamAsFailed = useCallback(() => {
      if (finishingRef.current || !examRef.current) return;
      finishingRef.current = true;
      const id = Number(examRef.current.id);
      timerRef.current && clearInterval(timerRef.current);
      goToResult(); // 🔄 tranziție
      (async () => {
         try {
            try {
               await failExam(id);
            } catch (e) {
            }
            try {
               const fresh = await getExam(id);
               setExam(fresh);
            } catch (e) {}
         } finally {
            finishingRef.current = false;
         }
      })();
   }, [goToResult]);

   const submitRemainingAsWrongHidden = useCallback(async (mapSnapshot) => {
      if (silentAutoSubmitRef.current) return;
      const liveExam = examRef.current;
      if (!liveExam) return;
      const examId = Number(liveExam.id);
      if (!Number.isInteger(examId) || examId <= 0) return;

      silentAutoSubmitRef.current = true;
      const snapshot = mapSnapshot || answersMapRef.current || {};
      const qs = Array.isArray(questionsRef.current) ? questionsRef.current : [];

      for (const q of qs) {
         const qid = Number(q?.id);
         if (!Number.isInteger(qid) || qid <= 0) continue;
         if (snapshot[qid]?.selected != null) continue;

         const answersLen = Array.isArray(q?.answers) ? q.answers.length : 0;
         if (answersLen <= 0) continue;

         let correctIdx = null;
         if (
            Object.prototype.hasOwnProperty.call(correctMapRef.current || {}, qid)
         ) {
            const ci = Number(correctMapRef.current[qid]);
            if (Number.isInteger(ci)) correctIdx = ci;
         }

         let wrongIdx0 = 0;
         if (Number.isInteger(correctIdx) && answersLen > 1) {
            wrongIdx0 = correctIdx === 0 ? 1 : 0;
            if (wrongIdx0 >= answersLen) wrongIdx0 = 0;
         }

         try {
            await submitExamAnswer(examId, {
               questionId: qid,
               // backend folosește one-based; nu actualizăm UI (hidden)
               selectedAnswer: wrongIdx0 + 1,
            });
         } catch (e) {
         }
      }
   }, []);

   const getEffectiveCorrectIdx = (qId) => {
      if (Object.prototype.hasOwnProperty.call(correctMap, qId))
         return Number(correctMap[qId]);
      const saved = answersMap[qId];
      if (saved?.serverCorrectIdx != null)
         return Number(saved.serverCorrectIdx);
      return null;
   };

   const onChoose = async (clientIdx0) => {
      if (!exam || !current) return;
      if (remaining <= 0) return;
      if (viewRef.current !== "test") return;

      const question = current;
      const questionId = Number(question.id);
      const questionIdx = idxRef.current;
      if (!Number.isInteger(questionId) || questionId <= 0) return;

      const liveMap = answersMapRef.current || {};
      const previousEntry = liveMap[questionId];
      const existing = previousEntry;
      if (existing && existing.selected != null) return;
      if (answerLoadingRef.current != null || finishingRef.current) return;
      answerLoadingRef.current = questionId;
      setAnswerLoading(questionId);

      const payload = {
         questionId,
         // EXAM backend tratează răspunsul selectat ca index one-based.
         selectedAnswer: Number(clientIdx0) + 1,
      };
      const localVerdict = verifyAnswerLocal(questionId, Number(clientIdx0));
      const optimisticCorrect = localVerdict === true ? true : null;

      let next = {
         ...liveMap,
         [questionId]: {
            selected: Number(clientIdx0),
            correct: optimisticCorrect,
            at: new Date().toISOString(),
         },
      };
      setAnswersMap(next);
      persistExamDraftNow({ answersMap: next, idx: questionIdx });
      let loadingCleared = false;

      try {
         const resp = await submitExamAnswer(Number(exam.id), payload);
         const serverCorrect =
            typeof resp?.correct === "boolean" ? resp.correct : null;
         const serverCorrectIdx = resolveServerCorrectIdx(
            resp?.correctAnswer ?? resp?.correctIndex ?? resp?.rightIndex,
            (question.answers || []).length,
            Number(clientIdx0),
            serverCorrect
         );
         const explanation = (resp?.explanation ?? "").trim() || null;

         if (Number.isInteger(serverCorrectIdx))
            setCorrectMap((m) => ({ ...m, [questionId]: serverCorrectIdx }));
         const finalCorrect =
            serverCorrect !== null ? serverCorrect : localVerdict;

         next = {
            ...next,
            [questionId]: {
               ...next[questionId],
               correct: finalCorrect,
               serverCorrectIdx: Number.isInteger(serverCorrectIdx)
                  ? serverCorrectIdx
                  : next[questionId]?.serverCorrectIdx ?? null,
               explanation,
            },
         };
         setAnswersMap(next);
         persistExamDraftNow({ answersMap: next, idx: questionIdx });

         const badNow =
            Object.values(next).filter(
               (a) => a?.selected != null && a.correct === false
            ).length || 0;
         if (badNow >= mistakeLimit) {
            answerLoadingRef.current = null;
            setAnswerLoading(null);
            loadingCleared = true;
            endExamAsFailed();
            return;
         }

         const correctNow =
            Object.values(next).filter(
               (a) => a?.selected != null && a.correct === true
            ).length || 0;
         if (correctNow >= passScore) {
            void submitRemainingAsWrongHidden(next);
            answerLoadingRef.current = null;
            setAnswerLoading(null);
            loadingCleared = true;
            goToResult();
            return;
         }

         answerLoadingRef.current = null;
         setAnswerLoading(null);
         loadingCleared = true;
         autoNext(next, questionIdx);
      } catch (e) {
         setAnswersMap((prev) => {
            const restored = { ...(prev || {}) };
            if (previousEntry && previousEntry.selected != null) {
               restored[questionId] = previousEntry;
            } else {
               delete restored[questionId];
            }
            return restored;
         });
         pushError(e?.message || "Nu am putut trimite răspunsul.");
      } finally {
         if (!loadingCleared) {
            answerLoadingRef.current = null;
            setAnswerLoading(null);
         }
      }
   };

   useEffect(() => {
      if (remaining === 0 && exam && view === "test") {
         timerRef.current && clearInterval(timerRef.current);
         goToResult(); // 🔄 tranziție când expiră timpul
      }
   }, [remaining, exam, view, goToResult]);

   const verdict = useMemo(() => {
      const failedByLives = mistakesMade >= mistakeLimit;
      const passed = !failedByLives && correctAnsweredRaw >= passScore;
      const failedByBackend =
         mistakesMade > Math.max(0, questions.length - passScore);
      const failedByTime = remaining === 0 && !passed;
      return passed
         ? "PASSED"
         : failedByLives || failedByBackend || failedByTime
         ? "FAILED"
         : "PASSED";
   }, [
      correctAnsweredRaw,
      mistakesMade,
      mistakeLimit,
      passScore,
      questions.length,
      remaining,
   ]);

   /* ===================== RENDER ===================== */
   // sus, lângă alte hooks:
   const fmtHistoryDate = React.useMemo(
      () =>
         new Intl.DateTimeFormat("ro-MD", {
            timeZone: "Europe/Chisinau",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
         }),
      []
   );
   const fmtHistoryTime = React.useMemo(
      () =>
         new Intl.DateTimeFormat("ro-MD", {
            timeZone: "Europe/Chisinau",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
         }),
      []
   );

   const allowed = computeIsAllowed(perm);

   const startDisabled = attemptsLoading || (baseShowIdnpGate && !hasIdnp);
   const startTitle = attemptsLoading
      ? t("start_title_checking")
      : baseShowIdnpGate && !hasIdnp
      ? t("start_title_save_idnp")
      : t("start_title_start");
   const imageDisplay = current?.image || NO_IMAGE_PLACEHOLDER_URL;
   const examRootClassName = ["student-exam", `student-exam--${view}`].join(
      " "
   );
   const examTestClassName =
      "student-exam__test-shell" +
      (resultTransitioning ? " student-exam__test-shell--hidden" : "");
   const resultCardClassName =
      "card top student-exam__resultCard" +
      (resultAnimOn ? " student-exam__resultCard--ready" : "");

   return (
      <div className={examRootClassName}>
         <AlertPills messages={pillMsgs} onDismiss={dismissLastPill} />

         {/* ===== WAITING ===== */}
         {view === "waiting" && resumeChecking && (
            <div className="card top">
               <p>{t("resume_checking")}</p>
            </div>
         )}

         {view === "waiting" && !resumeChecking && (
            <>
               <div className="card top">
                  {allowed ? (
                     <>
                        <h2>{t("permission_active")}</h2>
                        {/* ===== Selectare limbă înainte de start ===== */}

                        {/* === IDNP Gate (NUMAI dacă NU ai încercări) sau deschis manual === */}
                        {showIdnpGate && (
                           <div className="student-exam__idnp">
                              <div className="student-exam__idnp-row">
                                 {manualIdnpEditor && (
                                    <button
                                       className="student-exam__back bottom"
                                       onClick={() =>
                                          setManualIdnpEditor(false)
                                       }
                                       title="Închide editor IDNP"
                                    >
                                       <ReactSVG
                                          src={addIcon}
                                          className="student-exam__icon react-icon rotate45"
                                       />
                                    </button>
                                 )}
                                 <input
                                    id="idnp"
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    placeholder="IDNP-2001234567890"
                                    className="student-exam__input"
                                    value={idnp}
                                    onChange={(e) =>
                                       setIdnp(onlyDigits13(e.target.value))
                                    }
                                    onWheel={(e) => e.currentTarget.blur()}
                                    disabled={idnpBusy}
                                 />
                                 <button
                                    className="student-exam__back bottom green"
                                    onClick={async () => {
                                       setIdnpMsg(null);
                                       const clean = onlyDigits13(idnp);
                                       try {
                                          setIdnpBusy(true);
                                          const updated = await updateUser(
                                             Number(user.id),
                                             {
                                                idnp: clean,
                                             }
                                          );
                                          if (typeof setUser === "function") {
                                             setUser({
                                                ...user,
                                                ...updated,
                                                idnp: updated?.idnp ?? clean,
                                             });
                                          }
                                          setIdnpMsg({
                                             type: "success",
                                             text: "IDNP salvat.",
                                          });
                                          if (manualIdnpEditor)
                                             setManualIdnpEditor(false);
                                       } catch (e) {
                                          let tmsg = "Eroare la salvare.";
                                          try {
                                             const parsed = JSON.parse(
                                                String(e?.message || "{}")
                                             );
                                             if (parsed?.message)
                                                tmsg = Array.isArray(
                                                   parsed.message
                                                )
                                                   ? parsed.message.join(" ")
                                                   : parsed.message;
                                          } catch {}
                                          setIdnpMsg({
                                             type: "error",
                                             text: tmsg,
                                          });
                                       } finally {
                                          setIdnpBusy(false);
                                       }
                                    }}
                                    disabled={idnpBusy || !isIdnp13(idnp)}
                                    title={
                                       !isIdnp13(idnp)
                                          ? "IDNP trebuie să fie 13 cifre"
                                          : "Salvează"
                                    }
                                 >
                                    {idnpBusy ? "Se salvează…" : "Salvează"}
                                 </button>
                              </div>
                           </div>
                        )}

                        {perm?.validUntil && <p>{t("access_granted")}</p>}

                        <div
                           className="student-exam__lang"
                        >
                           <div className="student-exam__lang-buttons">
                              <button
                                 type="button"
                                 className={
                                    "student-exam__back bottom toggle" +
                                    (lang === "ro" ? " yellow" : "")
                                 }
                                 onClick={() => setLang("ro")}
                                 title="Română"
                              >
                                 RO
                              </button>
                              <button
                                 type="button"
                                 className={
                                    "student-exam__back bottom toggle" +
                                    (lang === "ru" ? " yellow" : "")
                                 }
                                 onClick={() => setLang("ru")}
                                 title="Русский"
                              >
                                 RU
                              </button>
                           </div>
                        </div>
                        <div className="student-exam__start-row">
                           <button
                              className="student-exam__back bottom green"
                              onClick={handleStart}
                              disabled={startDisabled}
                              title={startTitle}
                           >
                              {t("start_exam")}
                           </button>

                           {/* ✅ vizibil mereu, chiar dacă inputul este ascuns */}
                           {!showIdnpGate && (
                              <button
                                 className="student-exam__back bottom"
                                 onClick={() => {
                                    setManualIdnpEditor((v) => !v);
                                    setIdnpMsg(null);
                                 }}
                                 title={t("edit_idnp")}
                              >
                                 {t("edit_idnp")}
                              </button>
                           )}
                        </div>
                     </>
                  ) : (
                     <>
                    <h2>{t("no_permission_title")}</h2>
<p>{t("no_permission_body")}</p>

                        <button
                           onClick={async () => {
                              try {
                                 const p = await getMyPermissionStatus();
                                 setPerm(p);
                              } catch {}
                           }}
                           className="student-exam__back bottom"
                        >
                           {t("check_again")}
                        </button>

                        {showIdnpGate && (
                           <div className="student-exam__idnp student-exam__idnp--offset">
                              <div className="student-exam__idnp-row">
                                 <input
                                    id="idnp2"
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    placeholder="IDNP-2001234567890"
                                    className="student-exam__input student-exam__input--wide"
                                    value={idnp}
                                    onChange={(e) =>
                                       setIdnp(onlyDigits13(e.target.value))
                                    }
                                    onWheel={(e) => e.currentTarget.blur()}
                                    disabled={idnpBusy}
                                 />
                                 <button
                                    className="student-exam__back bottom green student-exam__back--offset"
                                    onClick={async () => {
                                       setIdnpMsg(null);
                                       const clean = onlyDigits13(idnp);
                                       try {
                                          setIdnpBusy(true);
                                          const updated = await updateUser(
                                             Number(user.id),
                                             {
                                                idnp: clean,
                                             }
                                          );
                                          if (typeof setUser === "function") {
                                             setUser({
                                                ...user,
                                                ...updated,
                                                idnp: updated?.idnp ?? clean,
                                             });
                                          }
                                          setIdnpMsg({
                                             type: "success",
                                             text: "IDNP salvat.",
                                          });
                                          if (manualIdnpEditor)
                                             setManualIdnpEditor(false);
                                       } catch (e) {
                                          let tmsg = "Eroare la salvare.";
                                          try {
                                             const parsed = JSON.parse(
                                                String(e?.message || "{}")
                                             );
                                             if (parsed?.message)
                                                tmsg = Array.isArray(
                                                   parsed.message
                                                )
                                                   ? parsed.message.join(" ")
                                                   : parsed.message;
                                          } catch {}
                                          setIdnpMsg({
                                             type: "error",
                                             text: tmsg,
                                          });
                                       } finally {
                                          setIdnpBusy(false);
                                       }
                                    }}
                                    disabled={idnpBusy || !isIdnp13(idnp)}
                                    title={
                                       !isIdnp13(idnp)
                                          ? "IDNP trebuie să fie 13 cifre"
                                          : "Salvează"
                                    }
                                 >
                                    {idnpBusy ? "Se salvează…" : "Salvează"}
                                 </button>
                              </div>
                              {idnpMsg && (
                                 <div
                                    className={
                                       "student-exam__idnp-msg" +
                                       (idnpMsg.type === "error"
                                          ? " student-exam__idnp-msg--error"
                                          : " student-exam__idnp-msg--success")
                                    }
                                 >
                                    {idnpMsg.text}
                                 </div>
                              )}
                           </div>
                        )}
                     </>
                  )}
               </div>

               {/* ISTORIC ÎNCERCĂRI */}
               <div className="card list">
                  <h4>{t("history_title")}</h4>
                  {attemptsLoading && <p>{t("loading_attempts")}</p>}
                  {attemptsError && <p>{attemptsError}</p>}
                  {!attemptsLoading &&
                     !attemptsError &&
                     attempts.length === 0 && <p>{t("no_attempts")}</p>}
                  {!attemptsLoading &&
                     !attemptsError &&
                     attempts.length > 0 && (
                        <div className="student-exam__history">
                           {attempts.slice(0, 20).map((a) => {
                              const statusKey = String(
                                 a.status || ""
                              ).toLowerCase();
                              const statusText =
                                 currentLang === "ru"
                                    ? ruStatus(statusKey)
                                    : roStatus(statusKey);

                              const startedDate = a.startedAt
                                 ? fmtHistoryDate.format(new Date(a.startedAt))
                                 : t("history_unknown");
                              const startedTime = a.startedAt
                                 ? fmtHistoryTime.format(new Date(a.startedAt))
                                 : t("history_unknown");
                              const finishedTime = a.finishedAt
                                 ? fmtHistoryTime.format(new Date(a.finishedAt))
                                 : t("history_open");
                              const scoreLabel =
                                 a.total != null
                                    ? `${a.score ?? 0}/${a.total}`
                                    : a.score != null
                                    ? String(a.score)
                                    : t("history_unknown");
                              const attemptLabel =
                                 a.attemptNumber != null
                                    ? `#${a.attemptNumber}`
                                    : null;

                              return (
                                 <div
                                    key={a.id}
                                    className={`student-exam__history-item student-exam__history-item--${statusKey}`}
                                 >
                                    <div className="student-exam__history-top">
                                       <div className="student-exam__history-status">
                                          {statusText}
                                       </div>
                                       {attemptLabel && (
                                          <div className="student-exam__history-pill student-exam__history-pill--attempt">
                                             {attemptLabel}
                                          </div>
                                       )}
                                    </div>

                                    <div className="student-exam__history-bottom">
                                       <div className="student-exam__history-badges">
                                          <div className="student-exam__history-pill student-exam__history-pill--score">
                                             {scoreLabel}
                                          </div>
                                       </div>

                                       <div className="student-exam__history-date">
                                          {startedDate} <span>•</span>{" "}
                                          {startedTime} <span>→</span>{" "}
                                          {finishedTime}
                                       </div>
                                    </div>
                                 </div>
                              );
                           })}
                        </div>
                     )}
               </div>
            </>
         )}

         {/* ===== TEST (cu fade out spre rezultat) ===== */}
         {view === "test" && exam && current && (
            <div className={examTestClassName}>
               <div className="student-exam__toolbar">
                  <div className="student-exam__toolbar-center">
                     <div className="student-exam__question-index">
                        {t("question")} {Math.min(idx + 1, total)}/{total}
                     </div>

                     {useHearts ? (
                        <div
                           className="lives__pill"
                           aria-label={`${t(
                              "lives"
                           )}: ${livesLeft}/${mistakeLimit}`}
                        >
                           {Array.from({ length: mistakeLimit }).map((_, i) => {
                              const lost = i < mistakesMade;
                              const pulse = i === mistakesMade - 1;
                              return (
                                 <ReactSVG
                                    key={i}
                                    src={lost ? heartCrackIcon : heartFullIcon}
                                    className={
                                       "lives__icon" +
                                       (lost
                                          ? " lives__icon--lost"
                                          : " lives__icon--full") +
                                       (pulse ? " lives__icon--pulse" : "")
                                    }
                                    beforeInjection={(svg) => {
                                       svg.setAttribute("aria-hidden", "true");
                                       svg.setAttribute("focusable", "false");
                                    }}
                                 />
                              );
                           })}
                        </div>
                     ) : (
                        <div
                           className="lives__pill lives__pill--dots"
                           aria-label={`${t(
                              "mistakes_label"
                           )}: ${mistakesMade}/${mistakeLimit}`}
                        >
                           {Array.from({ length: mistakeLimit }).map((_, i) => {
                              const active = i < mistakesMade;
                              return (
                                 <span
                                    key={i}
                                    className={
                                       "lives__dot" +
                                       (active ? " lives__dot--on" : "")
                                    }
                                 />
                              );
                           })}
                        </div>
                     )}
                  </div>

                  <div className="student-exam__timer">{prettyTime(remaining)}</div>
               </div>

               <div className="student-exam__statusboard">
                  {statusBoard.map(({ i, status }) => (
                     <button
                        key={i}
                        className={
                           "student-exam__dot" +
                           (i === idx ? " student-exam__dot--current" : "") +
                           (status === "ok" ? " student-exam__dot--ok" : "") +
                           (status === "bad" ? " student-exam__dot--bad" : "") +
                           (status === "answered"
                              ? " student-exam__dot--answered"
                              : "") +
                           (status === "none" ? " student-exam__dot--none" : "")
                        }
                        title={`${t("question")} ${i + 1}`}
                        onClick={() => jumpTo(i)}
                        disabled={answerLoading != null}
                     >
                        {i + 1}
                     </button>
                  ))}
               </div>

               <div className="student-exam__question">
                  <div className="student-exam__qtext" ref={qTextRef}>
                     {current.text}
                  </div>

                  <div className="student-exam__row">
                     <div className="student-exam__qimage-wrapper">
                        {imageDisplay && (
                           <img
                              key={`${exam.id}-${current.id}-${idx}`}
                              className="student-exam__qimage"
                              src={versionedImage(
                                 imageDisplay,
                                 `${exam.id}-${current.id}-${idx}`
                              )}
                              alt="Întrebare"
                              onError={onQuestionImageError}
                           />
                        )}
                     </div>

                     <div className="student-exam__answers-wrapper">
                        <div className="student-exam__answers">
                           {(current.answers || []).map((ans, i) => {
                              const saved = answersMap[current.id];
                              const already = !!saved && saved.selected != null;
                              const selectedIdx = already
                                 ? Number(saved.selected)
                                 : null;

                              const effectiveCorrectIdx = already
                                 ? getEffectiveCorrectIdx(current.id)
                                 : null;
                              const isCorrectOption =
                                 already &&
                                 Number.isInteger(effectiveCorrectIdx) &&
                                 i === effectiveCorrectIdx;
                              const isWrongSelected =
                                 already &&
                                 selectedIdx === i &&
                                 Number.isInteger(effectiveCorrectIdx) &&
                                 selectedIdx !== effectiveCorrectIdx;
                              const isSelectedNeutral =
                                 already &&
                                 selectedIdx === i &&
                                 !isCorrectOption &&
                                 !isWrongSelected;

                              const isBusy = answerLoading != null;

                              const className =
                                 "student-exam__answer" +
                                 (isCorrectOption
                                    ? " student-exam__answer--correct"
                                    : "") +
                                 (isWrongSelected
                                    ? " student-exam__answer--wrong-selected"
                                    : "") +
                                 (isSelectedNeutral
                                    ? " student-exam__answer--selected"
                                    : "") +
                                 (already
                                    ? " student-exam__answer--locked"
                                    : "") +
                                 (isBusy && !already
                                    ? " student-exam__answer--loading"
                                    : "");

                              return (
                                 <button
                                    key={i}
                                    className={className}
                                    onClick={() => onChoose(i)}
                                    disabled={already || isBusy}
                                    title={
                                       already
                                          ? t("answer_locked")
                                          : t("choose_answer")
                                    }
                                 >
                                    <span>{ans}</span>
                                 </button>
                              );
                           })}
                        </div>

                        <div className="student-exam__actions">
                           <button
                              type="button"
                              className="student-exam__back bottom"
                              onClick={goPrev}
                              disabled={idx === 0 || answerLoading != null}
                           >
                              {t("back")}
                           </button>
                           <div className="student-exam__spacer" />
                           <button
                              type="button"
                              className="student-exam__secondary student-exam__secondary--primary"
                              onClick={goNext}
                              disabled={idx >= total - 1 || answerLoading != null}
                           >
                              {t("next")}
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         )}

         {/* ===== RESULT ===== */}
         {view === "result" && exam && (
            <div className={resultCardClassName}>
               <h2
                  className={
                     "student-exam__resultVerdict" +
                     (verdict === "PASSED"
                        ? " student-exam__resultVerdict--passed"
                        : " student-exam__resultVerdict--failed")
                  }
               >
                  {verdict === "PASSED" ? "ADMIS" : "RESPINS"}
               </h2>

               <div
                  className="student-exam__resultHearts"
                  aria-label={`${t("mistakes_label")}: ${mistakesMade}/${mistakeLimit}`}
               >
                  {Array.from({ length: mistakeLimit }).map((_, i) => {
                     const cracked = i < mistakesMade;
                     return (
                        <ReactSVG
                           key={i}
                           src={cracked ? heartCrackIcon : heartFullIcon}
                           className={
                              "lives__icon lives__icon--result" +
                              (cracked
                                 ? " lives__icon--lost"
                                 : " lives__icon--full")
                           }
                           beforeInjection={(svg) => {
                              svg.setAttribute("aria-hidden", "true");
                              svg.setAttribute("focusable", "false");
                           }}
                        />
                     );
                  })}
               </div>

               <p className="student-exam__resultMeta">
                  {t("questions_count")}: <b>{total}</b> • {t("mistakes_made")}:{" "}
                  <b>{mistakesMade}</b> • {t("time_left")}:{" "}
                  <b>{prettyTime(remaining)}</b>
               </p>

               <button
                  onClick={() => {
                     if (user?.id) clearExamDraft(user.id);
                     setView("waiting");
                     setExam(null);
                     setQuestions([]);
                     setAnswersMap({});
                     setIdx(0);
                     setRemaining(0);
                     setPillMsgs([]);
                     silentAutoSubmitRef.current = false;
                     setExamUiLang(null); // eliberează „înghețarea” limbii UI
                  }}
                  className="student-exam__back bottom student-exam__back--result"
               >
                  {t("back_to_start")}
               </button>
            </div>
         )}
      </div>
   );
}
